import {
  useGetSubscriptionPlansQuery,
  useGetSubscriptionPlansByGroupQuery,
  useGetProviderSubscriptionsQuery,
} from "@/redux/apis/subscription/subscriptionPlansApi";
import { useOperator } from "./useOperator";
import { ALL_SUBSCRIPTION_STATUSES } from "./useOperatorAccess";

/** Slug of the default subscription group holding the standard public plans. */
const PUBLIC_GROUP_SLUG = "public";

/**
 * Group-aware subscription plans fetch for the provider UI (DEV-1395).
 *
 * A provider whose plan belongs to a non-public subscription group (e.g. the
 * hidden "enterprise-plus" group) must only see that group's plans — the
 * public list doesn't contain their plan, which both leaked the standard
 * plans to them and made their current plan render as "No active plan".
 *
 * The group is derived from the provider's MOST RECENT assignment across ALL
 * statuses (not just active ones): a lapsed or cancelled group provider still
 * belongs to their group. The query args mirror useOperatorAccess's
 * latest-assignment query so RTK Query serves both from one cache entry.
 *
 * Falls back to the standard public plans when there is no assignment or the
 * plan is in the public group.
 */
export const useSubscriptionPlans = () => {
  const operator = useOperator();

  const { data: latestSubscriptions, isLoading: isLoadingLatest } = useGetProviderSubscriptionsQuery(
    { providerId: operator?.id ?? "", status: [...ALL_SUBSCRIPTION_STATUSES], limit: 1, orderBy: ["startDate:DESC"] },
    { skip: !operator?.id }
  );

  const groupSlug = latestSubscriptions?.[0]?.subscription.subscriptionGroupSlug;
  const useGroupPlans = Boolean(groupSlug && groupSlug !== PUBLIC_GROUP_SLUG);

  // Wait for the assignment before fetching, otherwise a grouped provider
  // briefly loads (and caches) the wrong plan list.
  const isWaitingForAssignment = Boolean(operator?.id) && isLoadingLatest;

  const standardResult = useGetSubscriptionPlansQuery(undefined, {
    skip: isWaitingForAssignment || useGroupPlans,
  });
  const groupResult = useGetSubscriptionPlansByGroupQuery(
    { groupSlug: groupSlug ?? "" },
    { skip: !useGroupPlans }
  );

  const active = useGroupPlans ? groupResult : standardResult;

  return {
    data: active.data,
    isLoading: isWaitingForAssignment || active.isLoading,
    error: active.error,
    /** True when the plans shown are a non-public group's plans. */
    isGroupPlans: useGroupPlans,
    groupSlug: useGroupPlans ? groupSlug : undefined,
  };
};
